import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { LANGUAGE_CONFIG } from '../hooks/useLanguage';
import { cn } from '../lib/utils';
import { ChevronDownIcon, MenuIcon, CloseIcon, GlobeIcon } from './icons';
import type { Language } from '../types';

// CMS nav menu item from API
interface NavMenuPage {
  id: number;
  slug: string;
  title: string;
  showInMenu?: boolean;
}

interface NavbarProps {
  isScrolled: boolean;
  lang: Language;
  toggleLang: () => void;
  localizedPath: (path: string) => string;
}

const Navbar = memo<NavbarProps>(function Navbar({
  isScrolled,
  lang,
  toggleLang,
  localizedPath,
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const [caseStudiesOpen, setCaseStudiesOpen] = useState(false);
  const [articles, setArticles] = useState<NavMenuPage[]>([]);
  const [caseStudies, setCaseStudies] = useState<NavMenuPage[]>([]);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const articlesCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const caseStudiesCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const articlesDropdownRef = useRef<HTMLDivElement>(null);
  const caseStudiesDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch articles from API (only those marked showInMenu)
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`/api/pages/public/by-type/article/${lang}`);
        if (response.ok) {
          const data = await response.json();
          const allPages: NavMenuPage[] = data.pages || [];
          setArticles(allPages.filter(p => p.showInMenu));
        }
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      }
    };
    void fetchArticles();
  }, [lang]);

  // Fetch case studies from API (only those marked showInMenu)
  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await fetch(`/api/pages/public/by-type/case-study/${lang}`);
        if (response.ok) {
          const data = await response.json();
          const allPages: NavMenuPage[] = data.pages || [];
          setCaseStudies(allPages.filter(p => p.showInMenu));
        }
      } catch (error) {
        console.error('Failed to fetch case studies:', error);
      }
    };
    void fetchCaseStudies();
  }, [lang]);

  const t = translations[lang].nav;
  const servicesT = translations[lang].services;
  const location = useLocation();
  const isRTL = LANGUAGE_CONFIG[lang].isRTL;

  // Check if on home page (with or without language prefix)
  const pathWithoutLang = location.pathname.replace(/^\/(he|de)/, '') || '/';
  const isHome = pathWithoutLang === '/' || pathWithoutLang === '';

  // Get services from translations
  const services = [
    {
      href: 'ai-agents',
      label: servicesT.aiAgents.title,
    },
    {
      href: 'custom-development',
      label: servicesT.custom.title,
    },
    {
      href: 'integration-services',
      label: servicesT.integration.title,
    },
  ];


  // Check if current path matches a nav item
  const isActive = useCallback(
    (path: string) => {
      const cleanPath = path.startsWith('/') ? path : `/${path}`;
      return pathWithoutLang === cleanPath || pathWithoutLang === cleanPath.slice(1);
    },
    [pathWithoutLang]
  );

  const isServiceActive = services.some((s) => isActive(s.href));
  const isArticleActive = pathWithoutLang.startsWith('/articles') || articles.some((s) => isActive(`articles/${s.slug}`));
  const isCaseStudyActive = pathWithoutLang.startsWith('/case-studies') || caseStudies.some((s) => isActive(`case-studies/${s.slug}`));
  const isResourcesActive = pathWithoutLang.startsWith('/resources');

  const logoUrl = '/rp_logo.webp';

  const navClass = cn(
    'fixed w-full z-50 transition-all duration-300',
    isScrolled || !isHome
      ? 'bg-ink/80 backdrop-blur-xl border-b border-white/10 py-3'
      : 'bg-transparent py-5'
  );

  const linkClass = (active = false) =>
    cn(
      'font-medium text-white/80 transition-colors hover:text-brand-400',
      active && '!text-brand-400'
    );

  // Handle dropdown with delay on close
  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setServicesOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setServicesOpen(false);
    }, 150);
  }, []);

  // Articles dropdown handlers
  const handleArticlesMouseEnter = useCallback(() => {
    if (articlesCloseTimeoutRef.current) {
      clearTimeout(articlesCloseTimeoutRef.current);
      articlesCloseTimeoutRef.current = null;
    }
    setArticlesOpen(true);
  }, []);

  const handleArticlesMouseLeave = useCallback(() => {
    articlesCloseTimeoutRef.current = setTimeout(() => {
      setArticlesOpen(false);
    }, 150);
  }, []);

  // Case studies dropdown handlers
  const handleCaseStudiesMouseEnter = useCallback(() => {
    if (caseStudiesCloseTimeoutRef.current) {
      clearTimeout(caseStudiesCloseTimeoutRef.current);
      caseStudiesCloseTimeoutRef.current = null;
    }
    setCaseStudiesOpen(true);
  }, []);

  const handleCaseStudiesMouseLeave = useCallback(() => {
    caseStudiesCloseTimeoutRef.current = setTimeout(() => {
      setCaseStudiesOpen(false);
    }, 150);
  }, []);

  // Keyboard navigation for dropdown
  const handleDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setServicesOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
        setServicesOpen(false);
      } else if (e.key === 'ArrowDown' && servicesOpen) {
        e.preventDefault();
        const firstLink = dropdownRef.current?.querySelector('a');
        firstLink?.focus();
      }
    },
    [servicesOpen]
  );

  // Handle keyboard navigation within dropdown
  const handleDropdownItemKeyDown = useCallback(
    (e: React.KeyboardEvent, idx: number) => {
      if (e.key === 'Escape') {
        setServicesOpen(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const links = dropdownRef.current?.querySelectorAll('a');
        if (links && idx < links.length - 1) {
          (links[idx + 1] as HTMLElement).focus();
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const links = dropdownRef.current?.querySelectorAll('a');
        if (links && idx > 0) {
          (links[idx - 1] as HTMLElement).focus();
        }
      }
    },
    []
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setServicesOpen(false);
      }
      if (articlesDropdownRef.current && !articlesDropdownRef.current.contains(event.target as Node)) {
        setArticlesOpen(false);
      }
      if (caseStudiesDropdownRef.current && !caseStudiesDropdownRef.current.contains(event.target as Node)) {
        setCaseStudiesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (articlesCloseTimeoutRef.current) {
        clearTimeout(articlesCloseTimeoutRef.current);
      }
      if (caseStudiesCloseTimeoutRef.current) {
        clearTimeout(caseStudiesCloseTimeoutRef.current);
      }
    };
  }, []);

  // Mobile menu close with animation
  const handleMobileClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 200);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    if (isOpen) {
      handleMobileClose();
    } else {
      setIsOpen(true);
    }
  }, [isOpen, handleMobileClose]);

  return (
    <nav className={navClass} dir={isRTL ? 'rtl' : 'ltr'} aria-label="Main navigation">
      <div className="w-full px-6 sm:px-12 lg:px-16 xl:px-24">
        <div className="flex items-center justify-between">
          <div className="flex-shrink-0 flex items-center">
            <Link to={localizedPath('')} className="flex items-center gap-1 group">
              <img
                src={logoUrl}
                alt="Ronnie Projects"
                className="h-10 md:h-12 w-auto brightness-0 invert transition-all duration-300"
                width={488}
                height={476}
              />
              <span className="hidden sm:block font-heebo text-2xl font-normal whitespace-nowrap text-white pe-[10px]">
                {translations[lang].seo.siteName}
              </span>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
            {/* Services Dropdown */}
            <div
              ref={dropdownRef}
              className="relative py-2"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <a
                href={isHome ? '#services' : localizedPath('/#services')}
                onClick={(e) => {
                  e.preventDefault();
                  if (isHome) {
                    const element = document.getElementById('services');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  } else {
                    navigate(localizedPath('/#services'));
                  }
                }}
                onKeyDown={handleDropdownKeyDown}
                aria-expanded={servicesOpen}
                aria-haspopup="true"
                aria-controls="services-menu"
                className={cn(linkClass(isServiceActive), 'flex items-center gap-1')}
              >
                {t.services}
                <ChevronDownIcon
                  className={cn('w-4 h-4 transition-transform duration-200', servicesOpen && 'rotate-180')}
                />
              </a>

              <div
                id="services-menu"
                className={cn(
                  'absolute top-full left-0 rtl:left-auto rtl:right-0 pt-2 w-72 transition-all duration-200',
                  servicesOpen
                    ? 'opacity-100 visible translate-y-0'
                    : 'opacity-0 invisible -translate-y-2'
                )}
                role="menu"
                aria-orientation="vertical"
                aria-hidden={!servicesOpen}
              >
                <div className="bg-surface-raised rounded-2xl shadow-2xl border border-white/10 py-4">
                  {services.map((service, idx) => (
                    <Link
                      key={idx}
                      to={localizedPath(service.href)}
                      role="menuitem"
                      tabIndex={servicesOpen ? 0 : -1}
                      onKeyDown={(e) => handleDropdownItemKeyDown(e, idx)}
                      className={cn(
                        'block px-6 py-3 text-white/70 hover:bg-white/5 hover:text-white transition-colors font-medium',
                        isActive(service.href) && 'text-brand-400 bg-white/5'
                      )}
                    >
                      {service.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Articles Dropdown */}
            <div
              ref={articlesDropdownRef}
              className="relative py-2"
              onMouseEnter={handleArticlesMouseEnter}
              onMouseLeave={handleArticlesMouseLeave}
            >
              <Link
                to={localizedPath('articles')}
                aria-expanded={articlesOpen}
                aria-haspopup="true"
                aria-controls="articles-menu"
                className={cn(linkClass(isArticleActive), 'flex items-center gap-1')}
              >
                {t.articles}
                {articles.length > 0 && (
                  <ChevronDownIcon
                    className={cn('w-4 h-4 transition-transform duration-200', articlesOpen && 'rotate-180')}
                  />
                )}
              </Link>

              {articles.length > 0 && (
                <div
                  id="articles-menu"
                  className={cn(
                    'absolute top-full left-0 rtl:left-auto rtl:right-0 pt-2 w-72 transition-all duration-200',
                    articlesOpen
                      ? 'opacity-100 visible translate-y-0'
                      : 'opacity-0 invisible -translate-y-2'
                  )}
                  role="menu"
                  aria-orientation="vertical"
                  aria-hidden={!articlesOpen}
                >
                  <div className="bg-surface-raised rounded-2xl shadow-2xl border border-white/10 py-4">
                    {articles.map((article) => (
                      <Link
                        key={article.id}
                        to={localizedPath(`articles/${article.slug}`)}
                        role="menuitem"
                        tabIndex={articlesOpen ? 0 : -1}
                        className={cn(
                          'block px-6 py-3 text-white/70 hover:bg-white/5 hover:text-white transition-colors font-medium',
                          isActive(`articles/${article.slug}`) && 'text-brand-400 bg-white/5'
                        )}
                      >
                        {article.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Case Studies Dropdown */}
            <div
              ref={caseStudiesDropdownRef}
              className="relative py-2"
              onMouseEnter={handleCaseStudiesMouseEnter}
              onMouseLeave={handleCaseStudiesMouseLeave}
            >
              <Link
                to={localizedPath('case-studies')}
                aria-expanded={caseStudiesOpen}
                aria-haspopup="true"
                aria-controls="case-studies-menu"
                className={cn(linkClass(isCaseStudyActive), 'flex items-center gap-1')}
              >
                {t.caseStudies}
                {caseStudies.length > 0 && (
                  <ChevronDownIcon
                    className={cn('w-4 h-4 transition-transform duration-200', caseStudiesOpen && 'rotate-180')}
                  />
                )}
              </Link>

              {caseStudies.length > 0 && (
                <div
                  id="case-studies-menu"
                  className={cn(
                    'absolute top-full left-0 rtl:left-auto rtl:right-0 pt-2 w-72 transition-all duration-200',
                    caseStudiesOpen
                      ? 'opacity-100 visible translate-y-0'
                      : 'opacity-0 invisible -translate-y-2'
                  )}
                  role="menu"
                  aria-orientation="vertical"
                  aria-hidden={!caseStudiesOpen}
                >
                  <div className="bg-surface-raised rounded-2xl shadow-2xl border border-white/10 py-4">
                    {caseStudies.map((study) => (
                      <Link
                        key={study.id}
                        to={localizedPath(`case-studies/${study.slug}`)}
                        role="menuitem"
                        tabIndex={caseStudiesOpen ? 0 : -1}
                        className={cn(
                          'block px-6 py-3 text-white/70 hover:bg-white/5 hover:text-white transition-colors font-medium',
                          isActive(`case-studies/${study.slug}`) && 'text-brand-400 bg-white/5'
                        )}
                      >
                        {study.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to={localizedPath('resources')} className={linkClass(isResourcesActive)}>
              {t.resources}
            </Link>

            <Link to={localizedPath('about')} className={linkClass(isActive('about'))}>
              {t.about}
            </Link>

            <button
              onClick={toggleLang}
              aria-label={`Switch language to ${lang === 'he' ? 'English' : 'Hebrew'}`}
              className="flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 text-white/80 hover:bg-white/10 hover:text-white transition-all"
            >
              <GlobeIcon className="w-4 h-4" />
              <span className="text-sm font-bold uppercase" lang={lang === 'he' ? 'en' : 'he'}>{t.langToggle}</span>
            </button>

            {isHome ? (
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-brand text-white px-6 py-2 rounded-full hover:bg-brand-600 transition-all font-bold shadow-brand-sm"
              >
                {t.contact}
              </a>
            ) : (
              <Link
                to={localizedPath('contact')}
                className="bg-brand text-white px-6 py-2 rounded-full hover:bg-brand-600 transition-all font-bold shadow-brand-sm"
              >
                {t.contact}
              </Link>
            )}
          </div>

          <div className="md:hidden flex items-center gap-4">
            <button
              onClick={toggleLang}
              aria-label={`Switch language to ${lang === 'he' ? 'English' : 'Hebrew'}`}
              className="text-xs font-bold uppercase px-2 py-1 rounded border border-white/20 text-white/90"
            >
              <span lang={lang === 'he' ? 'en' : 'he'}>{t.langToggle}</span>
            </button>
            <button
              onClick={toggleMobileMenu}
              className="text-white"
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu with enter/exit animations */}
      {(isOpen || isClosing) && (
        <div
          id="mobile-menu"
          className={cn(
            'md:hidden bg-ink border-t border-white/10 absolute w-full top-full left-0 py-4 px-6 shadow-2xl transition-all duration-200',
            isClosing
              ? 'opacity-0 -translate-y-2'
              : 'opacity-100 translate-y-0 animate-in slide-in-from-top-2'
          )}
          role="menu"
        >
          <div className="flex flex-col space-y-4">
            {/* Services in mobile */}
            <div className="border-b border-white/10 pb-4">
              <p className="text-white/40 text-xs uppercase font-bold mb-3">{t.services}</p>
              {services.map((service, idx) => (
                <Link
                  key={idx}
                  to={localizedPath(service.href)}
                  onClick={handleMobileClose}
                  role="menuitem"
                  className={cn(
                    'block text-white/80 text-base font-medium py-2 hover:text-brand-400',
                    isActive(service.href) && 'text-brand-400'
                  )}
                >
                  {service.label}
                </Link>
              ))}
            </div>

            {/* Articles in mobile */}
            <div className="border-b border-white/10 pb-4">
              <Link
                to={localizedPath('articles')}
                onClick={handleMobileClose}
                role="menuitem"
                className={cn(
                  'block text-white/80 text-lg font-medium py-2 hover:text-brand-400',
                  isActive('articles') && 'text-brand-400'
                )}
              >
                {t.articles}
              </Link>
              {articles.length > 0 && (
                <div className="pl-4 rtl:pl-0 rtl:pr-4 mt-2">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={localizedPath(`articles/${article.slug}`)}
                      onClick={handleMobileClose}
                      role="menuitem"
                      className={cn(
                        'block text-white/50 text-base font-medium py-2 hover:text-brand-400',
                        isActive(`articles/${article.slug}`) && 'text-brand-400'
                      )}
                    >
                      {article.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Case Studies in mobile */}
            <div className="border-b border-white/10 pb-4">
              <Link
                to={localizedPath('case-studies')}
                onClick={handleMobileClose}
                role="menuitem"
                className={cn(
                  'block text-white/80 text-lg font-medium py-2 hover:text-brand-400',
                  isActive('case-studies') && 'text-brand-400'
                )}
              >
                {t.caseStudies}
              </Link>
              {caseStudies.length > 0 && (
                <div className="pl-4 rtl:pl-0 rtl:pr-4 mt-2">
                  {caseStudies.map((study) => (
                    <Link
                      key={study.id}
                      to={localizedPath(`case-studies/${study.slug}`)}
                      onClick={handleMobileClose}
                      role="menuitem"
                      className={cn(
                        'block text-white/50 text-base font-medium py-2 hover:text-brand-400',
                        isActive(`case-studies/${study.slug}`) && 'text-brand-400'
                      )}
                    >
                      {study.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to={localizedPath('resources')}
              onClick={handleMobileClose}
              role="menuitem"
              className={cn(
                'text-white/80 text-lg font-medium border-b border-white/10 pb-2',
                isActive('resources') && 'text-brand-400'
              )}
            >
              {t.resources}
            </Link>

            <Link
              to={localizedPath('about')}
              onClick={handleMobileClose}
              role="menuitem"
              className={cn(
                'text-white/80 text-lg font-medium border-b border-white/10 pb-2',
                isActive('about') && 'text-brand-400'
              )}
            >
              {t.about}
            </Link>

            {isHome ? (
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  handleMobileClose();
                  const element = document.getElementById('contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                role="menuitem"
                className="bg-brand text-white text-center px-6 py-4 rounded-xl font-bold"
              >
                {t.contact}
              </a>
            ) : (
              <Link
                to={localizedPath('contact')}
                onClick={handleMobileClose}
                role="menuitem"
                className="bg-brand text-white text-center px-6 py-4 rounded-xl font-bold"
              >
                {t.contact}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
});

export default Navbar;
